import * as i0 from '@angular/core';
import { ElementRef, Component, HostBinding, Input, ViewChild, EventEmitter, Optional, Inject, ContentChildren, Output, HostListener, NgModule } from '@angular/core';
import * as i5 from '@angular/common';
import { DOCUMENT, CommonModule } from '@angular/common';
import * as i2 from '@angular/cdk/observers';
import { ObserversModule } from '@angular/cdk/observers';
import { __decorate } from 'tslib';
import { LEFT_ARROW, RIGHT_ARROW } from '@angular/cdk/keycodes';
import { Default } from '@rogers/cdk/common/decorators';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swipe from 'swipejs';
import * as i1 from '@rogers/cdk/common/services';
import * as i2$1 from '@rogers/cdk/common/a11y';
import * as i3 from '@rogers/cdk/button';
import { ButtonModule } from '@rogers/cdk/button';
import * as i4 from '@rogers/cdk/icon';
import { IconModule } from '@rogers/cdk/icon';

let id = 0;
class CarouselSlideComponent {
    constructor(el, cdr) {
        this.el = el;
        this.cdr = cdr;
        this.twoSlides = false;
        this.refKey = `ref-key-${id++}`;
        this.transitionActive = false;
    }
    /** Fires changes in container collection after adding a new slide instance */
    ngOnInit() { }
    detectChanges() {
        if (this.cdr && !this.cdr.destroyed) {
            this.cdr.detectChanges();
        }
    }
    projectContentChanged() {
        const clonedElement = this.el.nativeElement.parentElement.querySelector(`[data-cloned="true"][data-ref-key="${this.refKey}"]`);
        if (clonedElement) {
            const clonedContentPortal = clonedElement.querySelector('[data-content-portal]');
            clonedContentPortal.innerHTML = '';
            this.contentPortal.nativeElement.childNodes.forEach((node) => {
                clonedContentPortal.appendChild(node.cloneNode(true));
            });
        }
    }
}
CarouselSlideComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: CarouselSlideComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
CarouselSlideComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: CarouselSlideComponent, selector: "ds-carousel-slide", inputs: { active: "active", ariaLabel: "ariaLabel" }, host: { properties: { "class.position-relative": "true", "class.ds-no-overflow": "true", "class.d-block": "true", "class.ds-carousel__slide": "true", "attr.aria-hidden": "active && !twoSlides ? false : true", "class.-active": "this.active", "attr.data-ref-key": "this.refKey" } }, viewQueries: [{ propertyName: "contentPortal", first: true, predicate: ["contentPortal"], descendants: true, read: ElementRef, static: true }], ngImport: i0, template: "<div\n  class=\"ds-carousel-slide__container h-100\"\n  [ngClass]=\"{ '-noDisplay': !active && !transitionActive }\"\n>\n  <div\n    class=\"w-100\"\n    (cdkObserveContent)=\"projectContentChanged()\"\n    [debounce]=\"300\"\n    data-content-portal\n    #contentPortal\n  >\n    <ng-content></ng-content>\n  </div>\n</div>\n", directives: [{ type: i5.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.CdkObserveContent, selector: "[cdkObserveContent]", inputs: ["cdkObserveContentDisabled", "debounce"], outputs: ["cdkObserveContent"], exportAs: ["cdkObserveContent"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: CarouselSlideComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ds-carousel-slide', host: {
                        '[class.position-relative]': 'true',
                        '[class.ds-no-overflow]': 'true',
                        '[class.d-block]': 'true',
                        '[class.ds-carousel__slide]': 'true',
                        '[attr.aria-hidden]': 'active && !twoSlides ? false : true',
                    }, template: "<div\n  class=\"ds-carousel-slide__container h-100\"\n  [ngClass]=\"{ '-noDisplay': !active && !transitionActive }\"\n>\n  <div\n    class=\"w-100\"\n    (cdkObserveContent)=\"projectContentChanged()\"\n    [debounce]=\"300\"\n    data-content-portal\n    #contentPortal\n  >\n    <ng-content></ng-content>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { active: [{
                type: HostBinding,
                args: ['class.-active']
            }, {
                type: Input
            }], ariaLabel: [{
                type: Input
            }], contentPortal: [{
                type: ViewChild,
                args: ['contentPortal', { static: true, read: ElementRef }]
            }], refKey: [{
                type: HostBinding,
                args: ['attr.data-ref-key']
            }] } });

const TRANSITION_SPEED = 500;
class CarouselComponent {
    constructor(el, cdr, windowService, focusMonitor, document) {
        this.el = el;
        this.cdr = cdr;
        this.windowService = windowService;
        this.focusMonitor = focusMonitor;
        this.document = document;
        this.destroyed$ = new Subject();
        this.slideIndexResetCalled = false;
        this.slideShowPaused = false;
        this.touch = { start: 0, end: 0 };
        this.isPauseByFocusMonitor = false;
        /** Will be emitted when active slide has been changed. Part of two-way-bindable [(activeSlide)] property */
        this.activeSlideChange = new EventEmitter(false);
        this.browsers = this.windowService.getBrowsers();
    }
    set contentSlides(slides) {
        this._slides = slides;
        this.initCarousel();
        setTimeout(() => {
            this.autoPlayCarousel();
        }, 0);
    }
    keyboardControl(event) {
        if (this.cursorKeysInteraction) {
            const code = event.keyCode || event.which;
            switch (code) {
                case RIGHT_ARROW:
                    this.next();
                    break;
                case LEFT_ARROW:
                    this.previous();
                    break;
                default:
                    break;
            }
        }
    }
    focus() {
        setTimeout(() => {
            if (!this.isMobile && this.focusOrigin !== 'mouse') {
                this.slidesWrapper.nativeElement.focus();
            }
        }, 0);
    }
    touchstart(event) {
        // Double click for apple mobile devices not working so added in if condition
        if (this.windowService.isDesktop()) {
            this.slides.forEach((slide) => (slide.transitionActive = true));
            // TouchEvent type fails in Edge, but swipe works when touch works with setting in about:flag
            if (this.windowService.isDesktop() && this.browsers['isChrome']) {
                if (event.changedTouches && event.changedTouches[0].pageX) {
                    this.touch.start = event.changedTouches[0].pageX;
                }
            }
            this.cdr.detectChanges();
        }
    }
    touchend(event) {
        // TouchEvent type fails in Edge, but swipe works when touch works with setting in about:flag
        if (this.windowService.isDesktop() && this.browsers['isChrome']) {
            if (event.changedTouches && event.changedTouches[0].pageX) {
                this.touch.end = event.changedTouches[0].pageX;
                if (this.touch.start !== this.touch.end) {
                    const touch = this.touch.end < this.touch.start ? 'left' : 'right';
                    touch === 'left' ? this.next() : this.previous();
                }
            }
        }
    }
    get slides() {
        return this._slides.toArray();
    }
    ngOnInit() {
        this.isMobile = this.windowService.isMobile();
        this.isBrowser = this.windowService.isBrowser();
        if (!this.isMobile) {
            setTimeout(() => {
                if (this.isBrowser)
                    this.focusSlidesWrapper();
                this.managingPlayPauseOnWrapperFocus();
                if (this.playPauseButton) {
                    this.playPauseButtonFocus();
                }
                this.cdr.markForCheck();
            }, 0);
        }
    }
    focusSlidesWrapper() {
        this.focusMonitor
            .monitor(this.slidesWrapper, true)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((origin) => {
            const element = this.slidesWrapper.nativeElement;
            if (origin === 'keyboard' &&
                this.document &&
                this.document.activeElement.isEqualNode(element)) {
                this.shadowOnFocus = true;
            }
            else {
                this.shadowOnFocus = false;
            }
            if (this.slides.length === 2 &&
                this.document &&
                !this.document.activeElement.isEqualNode(element)) {
                // Focusmonitor not monitoring cloned slides so, this is to add custom class for focus styling
                const focusedElement = this.slidesWrapper.nativeElement.getElementsByClassName('custom-focus')[0];
                if (origin === 'keyboard') {
                    if (focusedElement)
                        focusedElement.classList.remove(`cdk-keyboard-focused`, 'custom-focus');
                    this.document.activeElement.classList.add(`cdk-keyboard-focused`, 'custom-focus');
                }
                else {
                    if (focusedElement)
                        focusedElement.classList.remove('cdk-keyboard-focused', 'custom-focus');
                }
            }
        });
    }
    managingPlayPauseOnWrapperFocus() {
        this.focusMonitor
            .monitor(this.carouselWrapper, true)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((origin) => {
            this.focusOrigin = origin;
            // Pause carousel when focused by Tab key or inside accessibilty mode
            if (['keyboard', 'program'].indexOf(origin) > -1) {
                this.isPauseByFocusMonitor = true;
                this.pauseCarousel();
                this.slideShowPaused = true;
            }
            else {
                this.isPauseByFocusMonitor = false;
                this.resumeCarousel();
            }
        });
    }
    playPauseButtonFocus() {
        this.focusMonitor
            .monitor(this.playPauseButton, true)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((origin) => {
            // Pause carousel when focused by Tab key or inside accessibilty mode
            if (['keyboard', 'program', 'mouse'].indexOf(origin) > -1) {
                this.focusMonitor.stopMonitoring(this.carouselWrapper);
                this.isPauseByFocusMonitor = false;
            }
            else {
                this.managingPlayPauseOnWrapperFocus();
            }
        });
    }
    autoPlayCarousel() {
        if (!this.autoPlay || (this.isBrowser && this.isMobile)) {
            this.pauseCarousel();
            this.slideShowPaused = true;
            this.cdr.markForCheck();
        }
        else {
            this.resumeCarousel();
            this.slideShowPaused = false;
            this.cdr.markForCheck();
        }
    }
    onResize() {
        this.autoPlayCarousel();
    }
    initCarousel(autoRestart = true) {
        this.carouselOptions = {
            startSlide: 0,
            speed: TRANSITION_SPEED,
            auto: this.interval,
            draggable: true,
            autoRestart: autoRestart,
            continuous: this.loop,
            callback: (index) => {
                this.slideChanged(index);
            },
            transitionEnd: (i, el) => {
                this.slides.forEach((slide) => {
                    slide.transitionActive = false;
                });
            },
        };
        if (this.carousel) {
            this.carousel.kill();
            this.carousel = null;
        }
        switch (this.slides.length) {
            case 1:
                if (this.isBrowser)
                    this.slidesWrapper.nativeElement.className = '-singular';
                this.slides[0].active = true;
                this.slideAriaLabel = this.slides[0].ariaLabel;
                break;
            default:
                if (!this.slideIndexResetCalled) {
                    this.slideIndexResetCalled = true;
                    setTimeout(() => {
                        if (this.carousel) {
                            this.carousel.setup(this.carouselOptions);
                        }
                        else {
                            if (this.isBrowser) {
                                this.carousel = new Swipe(this.slidesWrapper.nativeElement, this.carouselOptions);
                            }
                        }
                        if (this.slides.length === 2) {
                            [].forEach.call(this.slides, function (slide) {
                                slide.twoSlides = true;
                            });
                        }
                        if (this.slides.length > 1) {
                            if (this.isBrowser)
                                this.slidesWrapper.nativeElement.classList.remove('-singular');
                            this.slideChanged(0);
                            this.slideShowPaused = false;
                            this.cdr.markForCheck();
                        }
                        this.slideIndexResetCalled = false;
                    }, 0);
                }
                break;
        }
    }
    slideChanged(gotoIndex) {
        this.slideAriaLabel = this.slides[gotoIndex].ariaLabel;
        if (this.slides.length > 0) {
            const currentActiveSlide = this.slides.find((slide) => {
                return slide.active === true;
            }, this);
            if (currentActiveSlide) {
                currentActiveSlide.active = false;
            }
            const nextActiveSlide = this.slides[gotoIndex];
            nextActiveSlide.active = true;
            this.activeSlideChange.emit({ newIndex: gotoIndex });
            this.updateSlideTransition(false);
        }
        // accessibility for 2 slides
        if (this.isBrowser && this.slides.length === 2) {
            this.handleTwoSlides();
        }
    }
    //Handle scenario for 2 slides for accessibility
    handleTwoSlides() {
        const slides = this.slidesWrapper.nativeElement
            .querySelectorAll('.ds-carousel__slide-wrapper')[0]
            .querySelectorAll('ds-carousel-slide');
        [].forEach.call(slides, function (slide) {
            if (slide.style.transform === 'translateX(0px)') {
                slide
                    .querySelectorAll('.ds-carousel-slide__container')[0]
                    .classList.remove('-noDisplay');
                slide.setAttribute('aria-hidden', 'false');
            }
            else {
                slide
                    .querySelectorAll('.ds-carousel-slide__container')[0]
                    .classList.add('-noDisplay');
                slide.setAttribute('aria-hidden', 'true');
            }
        });
    }
    resumeCarousel(event) {
        if (this.carousel && !this.isPauseByFocusMonitor) {
            this.carouselOptions.autoRestart = true;
            this.carousel.setup(this.carouselOptions);
            this.carousel.restart();
            if (this.slideShowPaused) {
                this.pauseCarousel();
            }
            this.cdr.detectChanges();
        }
    }
    pauseCarousel(event) {
        this.carouselOptions.autoRestart = false;
        if (this.carousel) {
            this.carousel.setup(this.carouselOptions);
            this.carousel.stop();
        }
        this.cdr.detectChanges();
    }
    togglePause() {
        if (this.slideShowPaused) {
            this.carouselOptions.autoRestart = true;
            this.carousel.setup(this.carouselOptions);
            this.carousel.restart();
        }
        else {
            this.carouselOptions.autoRestart = false;
            this.carousel.setup(this.carouselOptions);
            this.carousel.stop();
        }
        this.slideShowPaused = !this.slideShowPaused;
    }
    isLastSlide() {
        return (this.carousel &&
            this.carousel.getPos() === this.carousel.getNumSlides() - 1);
    }
    isFirstSlide() {
        return this.carousel && this.carousel.getPos() === 0;
    }
    next() {
        this.updateSlideTransition(true);
        this.carousel.next();
        this.focus();
    }
    previous() {
        this.updateSlideTransition(true);
        this.carousel.prev();
        this.focus();
    }
    navigateTo(index) {
        if (!this.slides[index].active) {
            this.updateSlideTransition(true);
            if (this.carousel) {
                this.carousel.slide(index, null);
            }
        }
        this.focus();
    }
    updateSlideTransition(state) {
        this.slides.forEach((slide) => {
            slide.transitionActive = state;
            slide.detectChanges();
        });
        if (this.cdr && !this.cdr.destroyed) {
            this.cdr.detectChanges();
        }
    }
    ngOnDestroy() {
        this.destroyed$.next(void 0);
        this.destroyed$.complete();
        this.focusMonitor.stopMonitoring(this.carouselWrapper);
        if (this.playPauseButton) {
            this.focusMonitor.stopMonitoring(this.playPauseButton);
        }
    }
}
CarouselComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: CarouselComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i1.WindowService }, { token: i2$1.FocusMonitor }, { token: DOCUMENT, optional: true }], target: i0.ɵɵFactoryTarget.Component });
CarouselComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.12", type: CarouselComponent, selector: "ds-carousel", inputs: { ariaLabel: "ariaLabel", showNextPrev: "showNextPrev", showPlayPause: "showPlayPause", showIndicators: "showIndicators", cursorKeysInteraction: "cursorKeysInteraction", autoPlay: "autoPlay", interval: "interval", loop: "loop", indicatorsPosition: "indicatorsPosition", controlsCopy: "controlsCopy" }, outputs: { activeSlideChange: "activeSlideChange" }, host: { listeners: { "keyup": "keyboardControl($event)", "touchstart": "touchstart($event)", "touchend": "touchend($event)", "window:resize": "onResize()" } }, queries: [{ propertyName: "contentSlides", predicate: CarouselSlideComponent }], viewQueries: [{ propertyName: "slidesWrapper", first: true, predicate: ["slidesWrapper"], descendants: true, static: true }, { propertyName: "carouselWrapper", first: true, predicate: ["carouselWrapper"], descendants: true, static: true }, { propertyName: "playPauseButton", first: true, predicate: ["playPauseButton"], descendants: true }], ngImport: i0, template: "<div\n  class=\"ds-carousel ds-keyboard-element-focus outline-none\"\n  tabindex=\"0\"\n  [attr.aria-label]=\"ariaLabel\"\n  role=\"group\"\n  #carouselWrapper\n>\n  <div\n    class=\"ds-carousel__content position-relative ds-no-overflow\"\n    [ngClass]=\"shadowOnFocus ? '-shadow' : ''\"\n  >\n    <div\n      class=\"position-absolute pl-24 z-1 m-auto left-0 top-0 bottom-0 d-none d-lg-flex align-items-center\"\n    >\n      <button\n        ds-button\n        variant=\"icon\"\n        class=\"-prev\"\n        (click)=\"previous()\"\n        id=\"prevIcon\"\n        [disabled]=\"!loop && isFirstSlide()\"\n        *ngIf=\"slides.length > 1 && showNextPrev\"\n      >\n        <ds-icon name=\"left\"></ds-icon>\n        {{ controlsCopy.prev }}\n      </button>\n    </div>\n    <div\n      #slidesWrapper\n      id=\"carouselBlock\"\n      class=\"outline-none\"\n      [attr.aria-label]=\"!isMobile ? slideAriaLabel : ''\"\n      [attr.tabindex]=\"!isMobile ? 0 : null\"\n      role=\"group\"\n    >\n      <div class=\"ds-carousel__slide-wrapper d-flex\">\n        <ng-content></ng-content>\n      </div>\n    </div>\n    <div\n      class=\"position-absolute pr-24 z-1 m-auto right-0 top-0 bottom-0 d-none d-lg-flex align-items-center\"\n    >\n      <button\n        ds-button\n        variant=\"icon\"\n        class=\"-next\"\n        (click)=\"next()\"\n        id=\"nextIcon\"\n        [disabled]=\"!loop && isLastSlide()\"\n        *ngIf=\"slides.length > 1 && showNextPrev\"\n      >\n        <ds-icon name=\"right\"></ds-icon>\n        {{ controlsCopy.next }}\n      </button>\n    </div>\n  </div>\n\n  <div\n    class=\"ds-carousel__indicators position-relative w-100 mx-auto px-8\"\n    *ngIf=\"slides.length > 1 && showIndicators\"\n  >\n    <div\n      class=\"d-flex align-items-center justify-content-center\"\n      [ngClass]=\"\n        this.indicatorsPosition === 'right'\n          ? 'justify-content-md-end'\n          : this.indicatorsPosition === 'left'\n          ? 'justify-content-md-start'\n          : 'justify-content-md-center'\n      \"\n    >\n      <div #playPauseButton>\n        <button\n          ds-button\n          variant=\"icon\"\n          class=\"ml-8\"\n          *ngIf=\"showPlayPause\"\n          (click)=\"togglePause()\"\n        >\n          <ds-icon\n            [name]=\"\n              !slideShowPaused && !isPauseByFocusMonitor ? 'pause' : 'play'\n            \"\n          ></ds-icon>\n          {{\n            !slideShowPaused && !isPauseByFocusMonitor\n              ? controlsCopy.pause\n              : controlsCopy.play\n          }}\n        </button>\n      </div>\n\n      <ng-container *ngIf=\"showIndicators\">\n        <button\n          ds-button\n          variant=\"icon\"\n          *ngFor=\"let s of slides; let i = index\"\n          [ngClass]=\"{\n            '-active': s.active === true,\n            '-nav': true\n          }\"\n          (click)=\"navigateTo(i)\"\n          [attr.aria-current]=\"s.active\"\n        >\n          {{ controlsCopy.indicator + ' ' + (i + 1) }}\n        </button>\n      </ng-container>\n    </div>\n  </div>\n</div>\n", components: [{ type: i3.ButtonComponent, selector: "button[ds-button], a[ds-button]", inputs: ["variant", "size", "alignIcon", "img", "transparent", "disabled", "tabIndex", "a11yDescription"] }, { type: i4.IconComponent, selector: "ds-icon", inputs: ["name", "color", "caption", "ariaHidden", "size"] }], directives: [{ type: i5.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i5.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i5.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] });
__decorate([
    Default(true)
], CarouselComponent.prototype, "showNextPrev", void 0);
__decorate([
    Default(true)
], CarouselComponent.prototype, "showPlayPause", void 0);
__decorate([
    Default(true)
], CarouselComponent.prototype, "showIndicators", void 0);
__decorate([
    Default(true)
], CarouselComponent.prototype, "cursorKeysInteraction", void 0);
__decorate([
    Default(true)
], CarouselComponent.prototype, "autoPlay", void 0);
__decorate([
    Default(5000)
], CarouselComponent.prototype, "interval", void 0);
__decorate([
    Default(true)
], CarouselComponent.prototype, "loop", void 0);
__decorate([
    Default('center')
], CarouselComponent.prototype, "indicatorsPosition", void 0);
__decorate([
    Default({
        next: 'Next',
        prev: 'Previous',
        play: 'Play',
        pause: 'Pause',
        indicator: 'Go to slide',
    })
], CarouselComponent.prototype, "controlsCopy", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: CarouselComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ds-carousel', template: "<div\n  class=\"ds-carousel ds-keyboard-element-focus outline-none\"\n  tabindex=\"0\"\n  [attr.aria-label]=\"ariaLabel\"\n  role=\"group\"\n  #carouselWrapper\n>\n  <div\n    class=\"ds-carousel__content position-relative ds-no-overflow\"\n    [ngClass]=\"shadowOnFocus ? '-shadow' : ''\"\n  >\n    <div\n      class=\"position-absolute pl-24 z-1 m-auto left-0 top-0 bottom-0 d-none d-lg-flex align-items-center\"\n    >\n      <button\n        ds-button\n        variant=\"icon\"\n        class=\"-prev\"\n        (click)=\"previous()\"\n        id=\"prevIcon\"\n        [disabled]=\"!loop && isFirstSlide()\"\n        *ngIf=\"slides.length > 1 && showNextPrev\"\n      >\n        <ds-icon name=\"left\"></ds-icon>\n        {{ controlsCopy.prev }}\n      </button>\n    </div>\n    <div\n      #slidesWrapper\n      id=\"carouselBlock\"\n      class=\"outline-none\"\n      [attr.aria-label]=\"!isMobile ? slideAriaLabel : ''\"\n      [attr.tabindex]=\"!isMobile ? 0 : null\"\n      role=\"group\"\n    >\n      <div class=\"ds-carousel__slide-wrapper d-flex\">\n        <ng-content></ng-content>\n      </div>\n    </div>\n    <div\n      class=\"position-absolute pr-24 z-1 m-auto right-0 top-0 bottom-0 d-none d-lg-flex align-items-center\"\n    >\n      <button\n        ds-button\n        variant=\"icon\"\n        class=\"-next\"\n        (click)=\"next()\"\n        id=\"nextIcon\"\n        [disabled]=\"!loop && isLastSlide()\"\n        *ngIf=\"slides.length > 1 && showNextPrev\"\n      >\n        <ds-icon name=\"right\"></ds-icon>\n        {{ controlsCopy.next }}\n      </button>\n    </div>\n  </div>\n\n  <div\n    class=\"ds-carousel__indicators position-relative w-100 mx-auto px-8\"\n    *ngIf=\"slides.length > 1 && showIndicators\"\n  >\n    <div\n      class=\"d-flex align-items-center justify-content-center\"\n      [ngClass]=\"\n        this.indicatorsPosition === 'right'\n          ? 'justify-content-md-end'\n          : this.indicatorsPosition === 'left'\n          ? 'justify-content-md-start'\n          : 'justify-content-md-center'\n      \"\n    >\n      <div #playPauseButton>\n        <button\n          ds-button\n          variant=\"icon\"\n          class=\"ml-8\"\n          *ngIf=\"showPlayPause\"\n          (click)=\"togglePause()\"\n        >\n          <ds-icon\n            [name]=\"\n              !slideShowPaused && !isPauseByFocusMonitor ? 'pause' : 'play'\n            \"\n          ></ds-icon>\n          {{\n            !slideShowPaused && !isPauseByFocusMonitor\n              ? controlsCopy.pause\n              : controlsCopy.play\n          }}\n        </button>\n      </div>\n\n      <ng-container *ngIf=\"showIndicators\">\n        <button\n          ds-button\n          variant=\"icon\"\n          *ngFor=\"let s of slides; let i = index\"\n          [ngClass]=\"{\n            '-active': s.active === true,\n            '-nav': true\n          }\"\n          (click)=\"navigateTo(i)\"\n          [attr.aria-current]=\"s.active\"\n        >\n          {{ controlsCopy.indicator + ' ' + (i + 1) }}\n        </button>\n      </ng-container>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i1.WindowService }, { type: i2$1.FocusMonitor }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; }, propDecorators: { contentSlides: [{
                type: ContentChildren,
                args: [CarouselSlideComponent]
            }], ariaLabel: [{
                type: Input
            }], showNextPrev: [{
                type: Input
            }], showPlayPause: [{
                type: Input
            }], showIndicators: [{
                type: Input
            }], cursorKeysInteraction: [{
                type: Input
            }], autoPlay: [{
                type: Input
            }], interval: [{
                type: Input
            }], loop: [{
                type: Input
            }], indicatorsPosition: [{
                type: Input
            }], controlsCopy: [{
                type: Input
            }], slidesWrapper: [{
                type: ViewChild,
                args: ['slidesWrapper', { static: true }]
            }], carouselWrapper: [{
                type: ViewChild,
                args: ['carouselWrapper', { static: true }]
            }], playPauseButton: [{
                type: ViewChild,
                args: ['playPauseButton', { static: false }]
            }], activeSlideChange: [{
                type: Output
            }], keyboardControl: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }], touchstart: [{
                type: HostListener,
                args: ['touchstart', ['$event']]
            }], touchend: [{
                type: HostListener,
                args: ['touchend', ['$event']]
            }], onResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

class CarouselModule {
}
CarouselModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: CarouselModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CarouselModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: CarouselModule, declarations: [CarouselComponent, CarouselSlideComponent], imports: [CommonModule, IconModule, ButtonModule, ObserversModule], exports: [CarouselComponent, CarouselSlideComponent] });
CarouselModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: CarouselModule, imports: [[CommonModule, IconModule, ButtonModule, ObserversModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.12", ngImport: i0, type: CarouselModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CarouselComponent, CarouselSlideComponent],
                    imports: [CommonModule, IconModule, ButtonModule, ObserversModule],
                    exports: [CarouselComponent, CarouselSlideComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CarouselComponent, CarouselModule, CarouselSlideComponent };
